// src/App.js
import React, { useState, useEffect } from 'react';
import FormBuilder from './components/FormBuilder';
import FormFiller from './components/FormFiller';
import TemplateList from './components/TemplateList';
import EditTemplate from './components/EditTemplate';
import LoginForm from './components/LoginForm';
import SignupForm from './components/SignupForm';
import LandingPage from './components/LandingPage';
import BlogList from './components/BlogList';
import BlogDetail from './components/BlogDetail';
import Unsubscribe from './components/Unsubscribe';
import Analytics from './components/Analytics';
import Support from './components/Support';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';
import config from './config';
import logo from './logo.svg';

const AppContent = () => {
  const [currentView, setCurrentView] = useState('templates');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const { isAuthenticated, user, logout } = useAuth();
  const [showBlog, setShowBlog] = useState(false);
  const [blogSlug, setBlogSlug] = useState(null);
  const [showBlogList, setShowBlogList] = useState(false);
  const [showUnsubscribe, setShowUnsubscribe] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(window.location.pathname);

  // ── NEW: dashboard mobile menu state ──
  const [dashMenuOpen, setDashMenuOpen] = useState(false);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const handlePathChange = () => {
      const path = window.location.pathname;
      setCurrentRoute(path);

      if (path === '/unsubscribe') {
        setShowLanding(false);
        setShowBlog(false);
        setShowLogin(false);
        setShowSignup(false);
        setShowBlogList(false);
        setBlogSlug(null);
        setShowUnsubscribe(true);
        return;
      }

      setShowUnsubscribe(false);

      if (path.startsWith('/blog/') && path !== '/blog') {
        const slug = path.replace('/blog/', '');
        setShowLanding(false);
        setShowLogin(false);
        setShowSignup(false);
        setShowBlog(true);
        setBlogSlug(slug);
        setShowBlogList(false);
      } else if (path === '/blog') {
        setShowLanding(false);
        setShowLogin(false);
        setShowSignup(false);
        setShowBlog(true);
        setShowBlogList(true);
        setBlogSlug(null);
      }
    };

    handlePathChange();
    window.addEventListener('popstate', handlePathChange);
    return () => window.removeEventListener('popstate', handlePathChange);
  }, []);

  const navigateToSupport = () => {
    setCurrentView('support');
    setSelectedTemplate(null);
    setSelectedTemplateId(null);
  };

  const handleBlogClick = () => {
    setShowLanding(false);
    setShowLogin(false);
    setShowSignup(false);
    setShowBlog(true);
    setShowBlogList(true);
    setBlogSlug(null);
  };

  const handleBlogPostClick = (slug) => {
    setBlogSlug(slug);
    setShowBlogList(false);
  };

  const handleBackToBlogList = () => {
    setShowBlogList(true);
    setBlogSlug(null);
  };

  const handleBackToHomeFromBlog = () => {
    setShowBlog(false);
    setShowBlogList(true);
    setBlogSlug(null);
    setShowLanding(true);
    setShowLogin(false);
    setShowSignup(false);
  };

  const handleLoginFromBlog = () => {
    setShowBlog(false);
    setShowBlogList(false);
    setBlogSlug(null);
    setShowLanding(false);
    setShowLogin(true);
    setShowSignup(false);
  };

  const handleSignupFromBlog = () => {
    setShowBlog(false);
    setShowBlogList(false);
    setBlogSlug(null);
    setShowLanding(false);
    setShowLogin(false);
    setShowSignup(true);
  };

  if (showUnsubscribe) return <Unsubscribe />;

  if (showBlog) {
    if (showBlogList) {
      return (
        <BlogList
          onBlogClick={handleBlogPostClick}
          onBackToHome={handleBackToHomeFromBlog}
          onLoginClick={handleLoginFromBlog}
          onSignupClick={handleSignupFromBlog}
        />
      );
    } else if (blogSlug) {
      return (
        <BlogDetail
          slug={blogSlug}
          onBack={handleBackToBlogList}
          onBlogClick={handleBlogPostClick}
          onLoginClick={handleLoginFromBlog}
          onSignupClick={handleSignupFromBlog}
          onNavigateToHome={handleBackToHomeFromBlog}
        />
      );
    }
  }

  const navigateToAnalytics = () => {
    setCurrentView('analytics');
    setSelectedTemplate(null);
    setSelectedTemplateId(null);
  };

  const navigateToTemplates = () => {
    setCurrentView('templates');
    setSelectedTemplate(null);
    setSelectedTemplateId(null);
  };

  const navigateToBuilder = () => {
    setCurrentView('builder');
    setSelectedTemplate(null);
    setSelectedTemplateId(null);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setSelectedTemplateId(template.id);
    setCurrentView('filler');
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setSelectedTemplateId(template.id);
    setCurrentView('edit');
  };

  const handleTemplateCreated = () => setCurrentView('templates');
  const handleTemplateUpdated = () => setCurrentView('templates');
  const handleCancelEdit = () => setCurrentView('templates');

  const handleGetStarted = () => {
    if (isAuthenticated) {
      setShowLanding(false);
      setShowLogin(false);
      setShowSignup(false);
      setCurrentView('templates');
    } else {
      setShowLogin(true);
      setShowLanding(false);
      setShowSignup(true);
    }
  };

  const handleLogin = () => {
    setShowLogin(true);
    setShowLanding(false);
    setShowSignup(false);
  };

  const handleSignup = () => {
    setShowLanding(false);
    setShowLogin(false);
    setShowSignup(true);
  };

  const handleLoginSuccess = () => {
    setShowLogin(false);
    setShowLanding(false);
    setShowSignup(false);
    setCurrentView('templates');
  };

  const handleSignupSuccess = () => {
    setShowLanding(false);
    setShowLogin(false);
    setShowSignup(false);
    setCurrentView('templates');
  };

  const handleBackToLanding = () => {
    setShowLanding(true);
    setShowLogin(false);
    setShowSignup(false);
  };

  const renderView = () => {
    switch (currentView) {
      case 'templates':
        return (
          <TemplateList
            onSelectTemplate={handleSelectTemplate}
            onEditTemplate={handleEditTemplate}
            onCreateNew={navigateToBuilder}
          />
        );
      case 'builder':
        return <FormBuilder onTemplateCreated={handleTemplateCreated} onBack={navigateToTemplates} />;
      case 'filler':
        if (!selectedTemplate) { setCurrentView('templates'); return null; }
        return <FormFiller template={selectedTemplate} onBack={navigateToTemplates} />;
      case 'edit':
        if (!selectedTemplateId) { setCurrentView('templates'); return null; }
        return (
          <EditTemplate
            templateId={selectedTemplateId}
            onTemplateUpdated={handleTemplateUpdated}
            onCancel={handleCancelEdit}
          />
        );
      case 'analytics':
        return <Analytics />;
      case 'support':
        return <Support />;
      default:
        return <TemplateList onSelectTemplate={handleSelectTemplate} />;
    }
  };

  if (showLanding && !isAuthenticated) {
    return <LandingPage onGetStarted={handleGetStarted} onLogin={handleLogin} onBlogClick={handleBlogClick} />;
  }

  if (showLogin) {
    return <LoginForm onLoginSuccess={handleLoginSuccess} onBack={handleBackToLanding} />;
  }

  if (showSignup) {
    return <SignupForm onSignupSuccess={handleSignupSuccess} onBack={handleBackToLanding} />;
  }

  if (isAuthenticated) {
    return (
      <div className="app-container">

        {/* ── Dashboard Nav ── */}
        <nav className="dashboard-nav">
          <div className="nav-container">

            {/* Logo — always visible */}
            <div className="nav-logo">
              <img src={logo} alt={`${config.APP_NAME} Logo`} className="logo-icon" width="45" height="45" />
              <span className="logo-text">{config.APP_NAME}</span>
            </div>

            {/* Desktop nav links */}
            <div className="nav-links desktop-nav-links">
              <button
                onClick={navigateToTemplates}
                className={`nav-link ${currentView === 'templates' ? 'active' : ''}`}
              >
                Templates
              </button>
              <button
                onClick={navigateToAnalytics}
                className={`nav-link ${currentView === 'analytics' ? 'active' : ''}`}
              >
                Analytics
              </button>
              <button
                onClick={() => setCurrentView('support')}
                className={`nav-link ${currentView === 'support' ? 'active' : ''}`}
              >
                Support
              </button>
            </div>

            {/* Desktop user info */}
            <div className="nav-user desktop-nav-user">
              <div className="user-avatar">
                <span className="avatar-text">{user?.username?.charAt(0).toUpperCase() || 'U'}</span>
              </div>
              <div className="user-dropdown">
                <span className="user-name">{user?.username}</span>
                <button onClick={logout} className="logout-btn">Logout</button>
              </div>
            </div>

            {/* Hamburger — mobile only */}
            <button
              className={`mobile-menu-btn ${dashMenuOpen ? 'open' : ''}`}
              onClick={() => setDashMenuOpen(prev => !prev)}
              aria-label="Toggle navigation menu"
              aria-expanded={dashMenuOpen}
            >
              <span className="hamburger-bar" />
              <span className="hamburger-bar" />
              <span className="hamburger-bar" />
            </button>

          </div>
        </nav>

        {/* ── Mobile Drawer — outside nav so it's never clipped ── */}
        <div className={`dash-mobile-drawer ${dashMenuOpen ? 'open' : ''}`}>

          {/* Nav links */}
          <div className="dash-drawer-links">
            <button
              onClick={() => { navigateToTemplates(); setDashMenuOpen(false); }}
              className={`nav-link ${currentView === 'templates' ? 'active' : ''}`}
            >
              Templates
            </button>
            <button
              onClick={() => { navigateToAnalytics(); setDashMenuOpen(false); }}
              className={`nav-link ${currentView === 'analytics' ? 'active' : ''}`}
            >
              Analytics
            </button>
            <button
              onClick={() => { setCurrentView('support'); setDashMenuOpen(false); }}
              className={`nav-link ${currentView === 'support' ? 'active' : ''}`}
            >
              Support
            </button>
          </div>

          {/* User + logout */}
          <div className="dash-drawer-user">
            <div className="user-avatar">
              <span className="avatar-text">{user?.username?.charAt(0).toUpperCase() || 'U'}</span>
            </div>
            <span className="user-name">{user?.username}</span>
            <button onClick={() => { logout(); setDashMenuOpen(false); }} className="logout-btn">
              Logout
            </button>
          </div>

        </div>

        {/* Backdrop */}
        {dashMenuOpen && (
          <div className="mobile-backdrop" onClick={() => setDashMenuOpen(false)} />
        )}

        {/* Main Content */}
        <main className="main-content">
          {renderView()}
        </main>

        {/* Footer */}
        <footer className="dashboard-footer">
          <div className="footer-content">
            <p>&copy; {currentYear} {config.APP_NAME}. All rights reserved.</p>
          </div>
        </footer>

      </div>
    );
  }

  return <LandingPage onGetStarted={handleGetStarted} onLogin={handleLogin} />;
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;